import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Warning, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const SEV_COLOR = { High: "#EF4444", Medium: "#F97316", Low: "#64748B" };

export default function NotificationBell() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const wrap = useRef();
  const navigate = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/alerts"); setAlerts(data); } catch (e) { /* silent */ }
  };
  useEffect(()=>{ load(); const t = setInterval(load, 20000); return ()=>clearInterval(t); }, []);
  useEffect(()=>{
    const c = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", c);
    return ()=>document.removeEventListener("click", c);
  }, []);

  const unread = alerts.filter(a=>!a.resolved);
  const resolve = (id, e) => { e.stopPropagation(); setAlerts(p=>p.map(a=>a.id===id?{...a,resolved:true}:a)); toast.success("Alert resolved"); };

  return (
    <div ref={wrap} className="relative">
      <button onClick={(e)=>{e.stopPropagation(); setOpen(!open);}} className="relative text-slate-600 hover:text-slate-900 transition-colors" data-testid="topbar-notifications">
        <Bell size={20} weight="duotone"/>
        {unread.length > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-[#EF4444] text-white text-[10px] rounded-full font-bold flex items-center justify-center ring-2 ring-white animate-pulse">
            {unread.length > 99 ? "99+" : unread.length}
          </span>
        )}
      </button>
      {open && (
        <>
          {/* mobile-only backdrop so the panel reads as a sheet */}
          <div className="fixed inset-0 bg-black/30 z-[1490] sm:hidden" onClick={()=>setOpen(false)}/>
          <div className="fixed inset-x-2 top-14 sm:absolute sm:inset-auto sm:top-full sm:right-0 sm:mt-2 sm:w-96 max-h-[78vh] sm:max-h-[500px] overflow-auto bg-white border border-slate-200 rounded-lg shadow-2xl z-[1500] scroll-thin">
            <div className="sticky top-0 bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-display font-bold text-sm">Notifications</div>
                <div className="text-[11px] text-slate-500 truncate">{unread.length} unresolved · {alerts.length} total</div>
              </div>
              <button onClick={()=>setOpen(false)} className="text-slate-400 shrink-0 ml-2"><X size={16}/></button>
            </div>
            {alerts.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No notifications</div>}
            {alerts.slice(0, 30).map(a => (
              <div key={a.id} role="button" tabIndex={0}
                onClick={()=>{setOpen(false); navigate("/alerts");}}
                onKeyDown={(e)=>{ if (e.key === "Enter") { setOpen(false); navigate("/alerts"); } }}
                className="w-full text-left px-3 sm:px-4 py-3 border-b border-slate-100 hover:bg-blue-50 transition-colors flex items-start gap-2 sm:gap-3 cursor-pointer">
                <div className={`w-1 self-stretch rounded-full shrink-0 ${a.resolved?"bg-slate-300":""}`} style={{background: !a.resolved ? SEV_COLOR[a.severity] : undefined}}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wide font-bold text-white px-1.5 py-0.5 rounded" style={{background: SEV_COLOR[a.severity]}}>{a.severity}</span>
                    <span className="font-semibold text-xs sm:text-sm truncate">{a.type}</span>
                  </div>
                  <div className="text-[11px] sm:text-xs text-slate-600 mt-0.5 break-words">{a.fleet_id} · {a.message}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{new Date(a.timestamp).toLocaleString()}</div>
                </div>
                {!a.resolved && <button onClick={(e)=>resolve(a.id,e)} className="text-[10px] px-2 py-1 border border-slate-200 rounded hover:bg-slate-100 shrink-0 self-start">Resolve</button>}
                {a.resolved && <span className="text-[10px] text-green-700 px-2 py-1 bg-green-50 rounded shrink-0 self-start">Resolved</span>}
              </div>
            ))}
            <button onClick={()=>{setOpen(false); navigate("/alerts");}} className="w-full text-center text-xs font-semibold text-[#1E3A8A] py-3 hover:bg-slate-50 sticky bottom-0 bg-white border-t border-slate-200">
              View All Alerts →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
